import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { Pagination, useMediaQuery, useTheme } from "@mui/material";

import {
  setLoading,
  getCacheKey,
  isCacheValid,
  setCacheEntry,
} from "slices/dashboardFilters";
import { loggedCloudRun } from "../../utils/apiLogger";
import { RootState } from "state";
import Layout from "components/Layout";
import KPICard from "../../BranchesDashboard/components/KPICard";
import LineChart from "../../BranchesDashboard/components/LineChart";
import BarChart from "../../BranchesDashboard/components/BarChart";
import DonutChart from "../../BranchesDashboard/components/DonutChart";
import {
  KPISkeleton,
  ChartSkeleton,
  ListSkeleton,
} from "../../BranchesDashboard/components/DashboardSkeleton";
import { OverviewData } from "../../BranchesDashboard/types";
import { getComparisonLabel } from "../../BranchesDashboard/utils/getComparisonLabel";
import { termsAndConditionsUrl, privacypolicyUrl } from "../../constants/urls";
import styles from "./index.module.css";

interface TopSellingItem {
  id: string;
  name: string;
  image?: string;
  coverPhoto?: string;
  description?: string;
  price: number;
  category?: string;
  orders: number;
  revenue: number;
}

/**
 * Combines responses from split endpoints into OverviewData structure
 */
const combineOverviewResponses = (
  metricsResult: any,
  revenueOverTimeResult: any,
  tablesOrOrderTimeResult: any
): OverviewData | null => {
  if (!metricsResult?.success || !metricsResult?.data) {
    return null;
  }

  const metrics = metricsResult.data;
  const revenueData = revenueOverTimeResult?.success
    ? revenueOverTimeResult.data
    : {};
  const tablesOrOrderData = tablesOrOrderTimeResult?.success
    ? tablesOrOrderTimeResult.data
    : {};

  // Determine hasDeliveryOrPickup from tables/order time response
  // If avgOrderTime exists, hasDeliveryOrPickup is true, otherwise false
  const hasDeliveryOrPickup = !!tablesOrOrderData.avgOrderTime;

  return {
    // From getDashboardMetrics
    totalRevenue: metrics.totalRevenue,
    totalOrders: metrics.totalOrders,
    avgOrderValue: metrics.avgOrderValue,
    paymentMethods: metrics.paymentMethods || [],

    // From getDashboardRevenueOverTime
    revenueOverTime: revenueData.revenueOverTime || [],
    avgRevenueByDay: revenueData.avgRevenueByDay || [],
    avgHourlySales: revenueData.avgHourlySales || [],

    // From getDashboardTablesOrOrderTime
    hasDeliveryOrPickup,
    ...(hasDeliveryOrPickup
      ? { avgOrderTime: tablesOrOrderData.avgOrderTime }
      : { tablesOpenPerDay: tablesOrOrderData.tablesOpenPerDay }),

    // Top selling items are fetched separately
    topSellingItems: [],
  };
};

/**
 * Normalizes backend response to extract topItems array consistently
 * Handles various response structures and always returns an array
 */
const normalizeTopItemsResponse = (result: any): TopSellingItem[] => {
  if (!result) return [];

  // Case 1: Direct array response
  if (Array.isArray(result)) {
    return result;
  }

  // Case 2: { success: true, data: { topSellingItems: [...] } }
  if (result.success && result.data) {
    const items =
      result.data.topSellingItems || // Actual API structure
      result.data.topItems ||
      result.data.items ||
      result.data.data ||
      (Array.isArray(result.data) ? result.data : []);

    return Array.isArray(items) ? items : [];
  }

  // Case 3: { success: true, data: [...] } (items directly in data)
  if (result.success && Array.isArray(result.data)) {
    return result.data;
  }

  return [];
};

/**
 * Extracts topItems from cached data structure
 */
const extractTopItemsFromCache = (cachedData: any): TopSellingItem[] => {
  if (!cachedData) return [];

  const items =
    cachedData.topSellingItems ||
    cachedData.topItems ||
    cachedData.items ||
    cachedData.data ||
    (Array.isArray(cachedData) ? cachedData : []);

  return Array.isArray(items) ? items : [];
};

const ChevronDownIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3 4.5L6 7.5L9 4.5"
      stroke="#656565"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronUpIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9 7.5L6 4.5L3 7.5"
      stroke="#656565"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Overview: React.FC = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    startDate,
    endDate,
    branchFilter: restaurantIds,
    loading,
    selectedDateOption,
  } = useSelector((state: RootState) => state.dashboardFilters);
  const currencySymbol = useSelector(
    (state: RootState) => state.profile.profiles[0]?.currencySymbol || "EGP"
  );

  // Initialize with null - will be set from cache or API
  const [data, setData] = useState<OverviewData | null>(null);
  const [allTopItems, setAllTopItems] = useState<TopSellingItem[]>([]); // Store all items from backend
  const [topItems, setTopItems] = useState<TopSellingItem[]>([]); // Current page items (5 items)
  const [totalTopItems, setTotalTopItems] = useState<number>(0);
  const [loadingState, setLoadingState] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [currentPage, setCurrentPage] = useState(1);
  const [refreshTrigger, setRefreshTrigger] = useState(0); // Trigger to force refresh
  const itemsPerPage = 5;
  const prefetchingRef = useRef<Set<number>>(new Set()); // Track which pages are being prefetched
  const dataCache = useSelector(
    (state: RootState) => state.dashboardFilters.dataCache || {}
  );

  // Sort state: { column: 'name' | 'revenue' | 'category' | 'amount', direction: 'asc' | 'dec' }
  const [sortState, setSortState] = useState<{
    column: string | null;
    direction: "asc" | "dec";
  }>({ column: null, direction: "asc" });

  // Separate loading states for progressive rendering
  const [loadingMetrics, setLoadingMetrics] = useState(true);
  const [loadingRevenue, setLoadingRevenue] = useState(true);
  const [loadingTablesOrOrder, setLoadingTablesOrOrder] = useState(true);
  const [loadingTopItems, setLoadingTopItems] = useState(true);
  const fetchingRef = useRef(false);
  const lastFetchKeyRef = useRef<string>("");
  const forceRefreshRef = useRef(false);
  // Track which restaurantIds the current active request is for
  const currentRequestRestaurantIdsRef = useRef<string>("");
  // Track which restaurantIds the currently displayed data belongs to
  const dataRestaurantIdsRef = useRef<string>("");
  // Track previous fetchKey to detect actual changes
  const prevFetchKeyRef = useRef<string>("");

  // Memoize restaurantIds to prevent unnecessary re-renders
  const restaurantIdsKey = useMemo(() => {
    const ids = restaurantIds || [];
    return Array.from(ids).sort().join(",");
  }, [restaurantIds]);

  // Helper functions for localStorage with 24h cache
  const getLocalStorageKey = (cacheKey: string) => {
    return `overview_data_${cacheKey}`;
  };

  const saveToLocalStorage = (
    cacheKey: string,
    data: OverviewData,
    timestamp?: number,
    restaurantIds?: string[]
  ) => {
    try {
      const storageKey = getLocalStorageKey(cacheKey);
      const now = timestamp || Date.now();
      const restaurantIdsKey = restaurantIds
        ? Array.from(restaurantIds).sort().join(",")
        : "";
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          data,
          timestamp: now,
          restaurantIds: restaurantIdsKey,
        })
      );
      console.log(
        "💾 [Overview] Saved to localStorage:",
        storageKey,
        "at",
        new Date(now),
        "restaurantIds:",
        restaurantIdsKey
      );
    } catch (error) {
      console.error("❌ [Overview] Failed to save to localStorage:", error);
    }
  };

  const loadFromLocalStorage = (
    cacheKey: string,
    currentRestaurantIds?: string[]
  ): { data: OverviewData; timestamp: number } | null => {
    try {
      const storageKey = getLocalStorageKey(cacheKey);
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours
        const age = Date.now() - parsed.timestamp;

        if (age < CACHE_DURATION) {
          // Verify restaurantIds match if provided
          if (currentRestaurantIds && currentRestaurantIds.length > 0) {
            const currentIdsKey = Array.from(currentRestaurantIds)
              .sort()
              .join(",");
            const cachedIdsKey = parsed.restaurantIds || "";
            
            if (cachedIdsKey && currentIdsKey !== cachedIdsKey) {
              console.warn(
                "⚠️ [Overview] Cache restaurantIds mismatch - Expected:",
                currentIdsKey,
                "Got:",
                cachedIdsKey,
                "- Clearing wrong cache"
              );
              localStorage.removeItem(storageKey);
              return null;
            }
          }
          
          console.log(
            "📦 [Overview] Loaded valid cache from localStorage:",
            storageKey,
            "age:",
            Math.floor(age / 1000 / 60),
            "minutes",
            "restaurantIds:",
            parsed.restaurantIds || "unknown"
          );
          return { data: parsed.data, timestamp: parsed.timestamp };
        } else {
          console.log(
            "⏰ [Overview] Cache expired for:",
            storageKey,
            "age:",
            Math.floor(age / 1000 / 60 / 60),
            "hours"
          );
          // Remove expired cache
          localStorage.removeItem(storageKey);
        }
      }
    } catch (error) {
      console.error("❌ [Overview] Failed to load from localStorage:", error);
    }
    return null;
  };

  const clearCurrentCacheOnly = (cacheKey: string) => {
    try {
      const storageKey = getLocalStorageKey(cacheKey);
      localStorage.removeItem(storageKey);
      console.log("🗑️ [Overview] Cleared cache for:", storageKey);
    } catch (error) {
      console.error("❌ [Overview] Failed to clear cache:", error);
    }
  };

  // Create a stable fetch key (exclude currentPage - pagination handled separately)
  const fetchKey = useMemo(() => {
    return `${restaurantIdsKey}-${startDate}-${endDate}`;
  }, [restaurantIdsKey, startDate, endDate]);

  // Helper function to get cache key for a specific page
  const getTopItemsCacheKey = useCallback(
    (page: number) => {
      if (!startDate || !endDate) return "";
      return `${getCacheKey(
        "getTopSellingItem",
        restaurantIds || [],
        startDate,
        endDate
      )}_page${page}_limit${itemsPerPage}`;
    },
    [restaurantIds, startDate, endDate, itemsPerPage]
  );

  // Helper function to fetch a specific page (used for both main fetch and prefetch)
  const fetchTopItemsPage = useCallback(
    async (
      page: number,
      isPrefetch: boolean = false
    ): Promise<{ items: TopSellingItem[]; total: number } | null> => {
      if (
        !startDate ||
        !endDate ||
        !restaurantIds ||
        restaurantIds.length === 0
      ) {
        return null;
      }

      const startDateObj = new Date(startDate);
      const endDateObj = new Date(endDate);
      const basePayload = {
        restaurantIds: Array.isArray(restaurantIds) ? restaurantIds : [],
        startDate: startDateObj.toISOString(),
        endDate: endDateObj.toISOString(),
        timezone: "Africa/Cairo",
      };

      const skip = (page - 1) * itemsPerPage;
      const cacheKey = getTopItemsCacheKey(page);

      // Check cache first
      const cachedData = (dataCache || {})[cacheKey];
      if (isCacheValid(cachedData)) {
        const cachedItems = extractTopItemsFromCache(cachedData.data);
        const cachedTotal = cachedData.data?.total || cachedItems.length;
        console.log(
          `✅ [Overview] Using cached page ${page} (${cachedItems.length} items)`
        );
        return { items: cachedItems, total: cachedTotal };
      }

      // Check if already prefetching this page
      if (prefetchingRef.current.has(page)) {
        console.log(`⏭️ [Overview] Already prefetching page ${page}`);
        return null;
      }

      try {
        if (isPrefetch) {
          prefetchingRef.current.add(page);
        }

        const topItemsResult = await loggedCloudRun(
          "getTopSellingItem",
          {
            ...basePayload,
            limit: itemsPerPage,
            skip: skip,
          },
          {
            type: isPrefetch ? "background" : "main",
            context: isPrefetch
              ? `Overview-prefetch-page-${page}`
              : `Overview-pagination-fetch-page-${page}`,
          }
        );

        const items = normalizeTopItemsResponse(topItemsResult);
        let totalCount = items.length;
        if (topItemsResult?.data?.total !== undefined) {
          totalCount = topItemsResult.data.total;
        } else if (topItemsResult?.total !== undefined) {
          totalCount = topItemsResult.total;
        } else if (topItemsResult?.data?.count !== undefined) {
          totalCount = topItemsResult.data.count;
        }

        // Cache the page
        dispatch(
          setCacheEntry({
            key: cacheKey,
            data: { topSellingItems: items, total: totalCount },
            branchIds: [...restaurantIds],
          })
        );

        if (isPrefetch) {
          console.log(
            `✅ [Overview] Prefetched page ${page} (${items.length} items, total: ${totalCount})`
          );
        }

        return { items, total: totalCount };
      } catch (err) {
        console.error(`❌ [Overview] Error fetching page ${page}:`, err);
        return null;
      } finally {
        if (isPrefetch) {
          prefetchingRef.current.delete(page);
        }
      }
    },
    [
      restaurantIds,
      startDate,
      endDate,
      itemsPerPage,
      dataCache,
      dispatch,
      getTopItemsCacheKey,
    ]
  );

  useEffect(() => {
    // If fetch key hasn't changed and we're not fetching, skip
    if (lastFetchKeyRef.current === fetchKey && !fetchingRef.current) {
      console.log("⏭️ [Overview] Skipping - fetch key unchanged:", fetchKey);
      return;
    }

    // If already fetching the same data, skip
    if (fetchingRef.current && lastFetchKeyRef.current === fetchKey) {
      console.log(
        "⏭️ [Overview] Skipping - already fetching same data:",
        fetchKey
      );
      return;
    }

    const fetchData = async () => {
      // Double-check: if fetch key changed while we were waiting, skip
      if (lastFetchKeyRef.current === fetchKey && fetchingRef.current) {
        console.log("⏭️ [Overview] Skipping - duplicate call prevented");
        return;
      }

      // Check if fetchKey actually changed - if so, clear old data first
      const prevFetchKey = prevFetchKeyRef.current;
      const fetchKeyChanged = prevFetchKey !== fetchKey;
      
      if (fetchKeyChanged && prevFetchKey !== "") {
        // Filters changed - clear old data immediately to prevent showing wrong branch data
        console.log(
          "🔄 [Overview] Filters changed - clearing old data before fetch"
        );
        fetchingRef.current = false;
        lastFetchKeyRef.current = "";
        currentRequestRestaurantIdsRef.current = "";
        dataRestaurantIdsRef.current = "";
        setData(null);
        setAllTopItems([]);
        setTopItems([]);
        setTotalTopItems(0);
        setLoadingState(true);
        setLoadingMetrics(true);
        setLoadingRevenue(true);
        setLoadingTablesOrOrder(true);
        setLoadingTopItems(true);
      }

      // Update fetch key and flag immediately to prevent duplicate calls
      const currentFetchKey = fetchKey;
      lastFetchKeyRef.current = currentFetchKey;
      prevFetchKeyRef.current = currentFetchKey;
      fetchingRef.current = true;

      console.log("🔄 [Overview] Starting fetch with key:", {
        fetchKey: currentFetchKey,
        startDate,
        endDate,
        restaurantIds: restaurantIdsKey,
      });

      // If branches are still loading, wait for them to load first
      if (loading) {
        console.log("⏳ [Overview] Waiting for branches to load...");
        fetchingRef.current = false;
        return;
      }

      // Only skip if we have no restaurants AND branches have finished loading
      if (
        !restaurantIds ||
        restaurantIds.length === 0 ||
        !startDate ||
        !endDate
      ) {
        console.warn(
          "⚠️ [Overview] No restaurants selected or date range missing"
        );
        setLoadingState(false);
        setData(null);
        fetchingRef.current = false;
        return;
      }

      // Check localStorage for 24h cache first - ALWAYS check before any API calls
      // Skip cache check if forceRefresh is true (refresh button was clicked)
      const overviewCacheKey = getCacheKey(
        "getDashboardOverview",
        restaurantIds || [],
        startDate,
        endDate
      );
      const cachedResult = loadFromLocalStorage(overviewCacheKey, restaurantIds);

      // If valid 24h cache exists and not forcing refresh, use it and skip ALL API calls
      if (cachedResult && !forceRefreshRef.current) {
        const currentIdsKey = Array.from(restaurantIds || [])
          .sort()
          .join(",");
        
        console.log(
          "✅ [Overview] Using valid 24h cached data - skipping all API calls",
          "restaurantIds verified:",
          currentIdsKey
        );
        setData(cachedResult.data);
        // Track which restaurantIds this cached data belongs to
        dataRestaurantIdsRef.current = currentIdsKey;
        setLastUpdated(new Date(cachedResult.timestamp));
        setLoadingState(false);
        setLoadingMetrics(false);
        setLoadingRevenue(false);
        setLoadingTablesOrOrder(false);
        setLoadingTopItems(false);
        dispatch(setLoading(false));
        fetchingRef.current = false;

        // Also set topItems if available (stored in cache but not in OverviewData type)
        const cachedDataWithTopItems = cachedResult.data as OverviewData & {
          topItems?: TopSellingItem[];
        };
        if (
          cachedDataWithTopItems?.topItems &&
          Array.isArray(cachedDataWithTopItems.topItems)
        ) {
          setAllTopItems(cachedDataWithTopItems.topItems);
          setTotalTopItems(cachedDataWithTopItems.topItems.length);
        }
        return; // Exit early - no API calls needed
      }

      // Check cache for each endpoint separately
      const metricsCacheKey = getCacheKey(
        "getDashboardMetrics",
        restaurantIds || [],
        startDate,
        endDate
      );
      const revenueCacheKey = getCacheKey(
        "getDashboardRevenueOverTime",
        restaurantIds || [],
        startDate,
        endDate
      );
      const tablesOrOrderCacheKey = getCacheKey(
        "getDashboardTablesOrOrderTime",
        restaurantIds || [],
        startDate,
        endDate
      );
      const topItemsCacheKey = getCacheKey(
        "getTopSellingItem",
        restaurantIds || [],
        startDate,
        endDate
      ); // No page number in key since we store all items

      const cachedMetrics = (dataCache || {})[metricsCacheKey];
      const cachedRevenue = (dataCache || {})[revenueCacheKey];
      const cachedTablesOrOrder = (dataCache || {})[tablesOrOrderCacheKey];
      const cachedTopItems = (dataCache || {})[topItemsCacheKey];

      const hasValidMetricsCache = isCacheValid(cachedMetrics);
      const hasValidRevenueCache = isCacheValid(cachedRevenue);
      const hasValidTablesOrOrderCache = isCacheValid(cachedTablesOrOrder);
      const hasValidTopItemsCache = isCacheValid(cachedTopItems);

      // Set loading states based on cache availability
      setLoadingMetrics(!hasValidMetricsCache);
      setLoadingRevenue(!hasValidRevenueCache);
      setLoadingTablesOrOrder(!hasValidTablesOrOrderCache);
      setLoadingTopItems(!hasValidTopItemsCache);

      // Combine cached data if available (even partial cache)
      // This ensures UI is displayed immediately with any available cached data
      if (
        hasValidMetricsCache ||
        hasValidRevenueCache ||
        hasValidTablesOrOrderCache
      ) {
        console.log("✅ [Overview] Using cached data (partial or full)");

        // Use cached data, filling in missing parts with empty/default values
        const combinedData = combineOverviewResponses(
          hasValidMetricsCache
            ? {
                success: true,
                data: cachedMetrics.data.metrics || cachedMetrics.data,
              }
            : { success: false, data: null },
          hasValidRevenueCache
            ? {
                success: true,
                data: cachedRevenue.data.revenue || cachedRevenue.data,
              }
            : { success: false, data: null },
          hasValidTablesOrOrderCache
            ? {
                success: true,
                data:
                  cachedTablesOrOrder.data.tablesOrOrder ||
                  cachedTablesOrOrder.data,
              }
            : { success: false, data: null }
        );

        if (combinedData) {
          setData(combinedData);
          // If we have at least metrics cache, we can show the UI
          if (hasValidMetricsCache) {
            setLoadingState(false);
            dispatch(setLoading(false));
          }
        }

        // Extract topSellingItems from cache if available (cache now stores all items)
        if (hasValidTopItemsCache) {
          const cachedItems = extractTopItemsFromCache(cachedTopItems.data);
          const cachedTotal = cachedTopItems.data?.total || cachedItems.length;
          console.log(
            `✅ [Overview] Loaded ${cachedItems.length} top selling items from cache (total: ${cachedTotal})`
          );
          setAllTopItems(cachedItems);
          setTotalTopItems(cachedTotal);
          // Reset to page 1 when loading from cache - pagination effect will update topItems
          setCurrentPage(1);
        } else {
          setAllTopItems([]);
          setTopItems([]);
          setTotalTopItems(0);
        }
      }

      // If we have valid cache for all parts and not forcing refresh, show UI immediately
      // Note: We don't do background refresh - we use cache for 24h as per requirements
      if (
        hasValidMetricsCache &&
        hasValidRevenueCache &&
        hasValidTablesOrOrderCache &&
        !forceRefreshRef.current
      ) {
        setLoadingState(false);
        dispatch(setLoading(false));
        fetchingRef.current = false;
        return; // Exit early - use cached data, no API calls
      }

      console.log("🔍 [Overview] Starting data fetch with split endpoints...");
      setLoadingState(true);
      setError(null);

      // Reset loading states
      setLoadingMetrics(true);
      setLoadingRevenue(true);
      setLoadingTablesOrOrder(true);
      setLoadingTopItems(true);

      try {
        // Convert moment timestamps to Date objects
        const startDateObj = new Date(startDate);
        const endDateObj = new Date(endDate);

        // Ensure restaurantIds is a proper array
        const restaurantIdsArray = Array.isArray(restaurantIds)
          ? restaurantIds
          : [];
        const basePayload = {
          restaurantIds: restaurantIdsArray,
          startDate: startDateObj.toISOString(),
          endDate: endDateObj.toISOString(),
          timezone: "Africa/Cairo",
        };

        // Capture current restaurantIds to verify responses match this request
        // This prevents race conditions when user switches branches quickly
        const requestRestaurantIdsKey = Array.from(restaurantIdsArray)
          .sort()
          .join(",");
        currentRequestRestaurantIdsRef.current = requestRestaurantIdsKey;
        console.log(
          "🔒 [Overview] Locking request for restaurantIds:",
          requestRestaurantIdsKey
        );

        // Helper function to verify response belongs to the current active request
        // We check if the request's restaurantIds is still the active one
        // This prevents stale responses from being processed when user switches restaurants
        const verifyRestaurantIdsMatch = (): boolean => {
          // The response belongs to the request if the request's restaurantIds is still active
          // OR if no new request has been made (currentRequestRestaurantIdsRef is still set to this request)
          const currentIdsKey = Array.from(restaurantIds || [])
            .sort()
            .join(",");
          const requestIdsKey = currentRequestRestaurantIdsRef.current;
          
          // Response is valid if:
          // 1. Current state matches the request (normal case)
          // 2. OR the request is still the active one (no new request made yet)
          const matches = currentIdsKey === requestIdsKey;
          
          if (!matches) {
            console.warn(
              "⚠️ [Overview] RestaurantIds changed - ignoring response. Request:",
              requestIdsKey,
              "Current:",
              currentIdsKey
            );
          }
          return matches;
        };

        console.log("📤 [Overview] Calling split endpoints with:", basePayload);
        console.log(
          "📋 [Overview] Restaurant IDs being sent:",
          restaurantIdsArray
        );

        // Fetch metrics first - display KPI cards as soon as ready (IMMEDIATE, no delay)
        loggedCloudRun("getDashboardMetrics", basePayload, {
          type: "main",
          context: "Overview-main-fetch",
        })
          .then((metricsResult) => {
            console.log("📥 [Overview] Metrics response:", metricsResult);
            setLoadingMetrics(false);

            // Verify restaurantIds haven't changed before processing response
            if (!verifyRestaurantIdsMatch()) {
              console.log(
                "⏭️ [Overview] Ignoring metrics response - restaurantIds changed"
              );
              return;
            }

            if (metricsResult?.success && metricsResult?.data) {
              // Update data with metrics using functional update to preserve existing data
              setData((prevData) => {
                // Only merge with prevData if it belongs to the same restaurantIds
                const shouldUsePrevData =
                  prevData &&
                  dataRestaurantIdsRef.current === requestRestaurantIdsKey;
                const baseData: OverviewData = shouldUsePrevData
                  ? prevData
                  : {
                  totalRevenue: {
                    current: 0,
                    previous: 0,
                    change: 0,
                    label: null,
                  },
                  totalOrders: {
                    current: 0,
                    previous: 0,
                    change: 0,
                    label: null,
                  },
                  avgOrderValue: {
                    current: 0,
                    previous: 0,
                    change: 0,
                    label: null,
                  },
                  paymentMethods: [],
                  hasDeliveryOrPickup: false, // Will be updated when tables/order time loads
                  revenueOverTime: [],
                  avgRevenueByDay: [],
                  avgHourlySales: [],
                  topSellingItems: [],
                };

                // Update tracking ref to indicate this data belongs to current restaurantIds
                dataRestaurantIdsRef.current = requestRestaurantIdsKey;

                return {
                  ...baseData,
                  totalRevenue: metricsResult.data.totalRevenue,
                  totalOrders: metricsResult.data.totalOrders,
                  avgOrderValue: metricsResult.data.avgOrderValue,
                  paymentMethods: metricsResult.data.paymentMethods || [],
                  // hasDeliveryOrPickup will be determined when tables/order time data loads
                };
              });

              // Cache metrics
              dispatch(
                setCacheEntry({
                  key: metricsCacheKey,
                  data: { metrics: metricsResult.data },
                  branchIds: [...restaurantIds],
                })
              );
              console.log("✅ [Overview] Metrics loaded - KPI Cards displayed");

              // Set main loading state to false once metrics are loaded (critical data)
              // Update lastUpdated to current time when fetching fresh data
              setLastUpdated(new Date());
              setLoadingState(false);
              dispatch(setLoading(false));
            }
          })
          .catch((err) => {
            console.error("❌ [Overview] Metrics error:", err);
            setLoadingMetrics(false);
            setLoadingState(false);
            dispatch(setLoading(false));
          });

        // Delay other requests slightly to prioritize metrics (background requests)
        // Use a small delay (100ms) so they don't compete with the first request
        setTimeout(() => {
          // Fetch revenue over time - display charts as soon as ready
          loggedCloudRun("getDashboardRevenueOverTime", basePayload, {
            type: "main",
            context: "Overview-main-fetch",
          })
            .then((revenueResult) => {
              console.log("📥 [Overview] Revenue response:", revenueResult);
              setLoadingRevenue(false);

              // Verify restaurantIds haven't changed before processing response
              if (!verifyRestaurantIdsMatch()) {
                console.log(
                  "⏭️ [Overview] Ignoring revenue response - restaurantIds changed"
                );
                return;
              }

              if (revenueResult?.success && revenueResult?.data) {
                // Update data with revenue using functional update
                setData((prevData) => {
                  // Only merge with prevData if it belongs to the same restaurantIds
                  const shouldUsePrevData =
                    prevData &&
                    dataRestaurantIdsRef.current === requestRestaurantIdsKey;
                  if (!shouldUsePrevData) {
                    dataRestaurantIdsRef.current = requestRestaurantIdsKey;
                  }
                  // If no base data yet or data belongs to different restaurantIds, create minimal structure
                  if (!shouldUsePrevData) {
                    return {
                      totalRevenue: {
                        current: 0,
                        previous: 0,
                        change: 0,
                        label: null,
                      },
                      totalOrders: {
                        current: 0,
                        previous: 0,
                        change: 0,
                        label: null,
                      },
                      avgOrderValue: {
                        current: 0,
                        previous: 0,
                        change: 0,
                        label: null,
                      },
                      paymentMethods: [],
                      hasDeliveryOrPickup: false, // Will be updated when tables/order time loads
                      revenueOverTime: revenueResult.data.revenueOverTime || [],
                      avgRevenueByDay: revenueResult.data.avgRevenueByDay || [],
                      avgHourlySales: revenueResult.data.avgHourlySales || [],
                      topSellingItems: [],
                    };
                  }
                  return {
                    ...prevData,
                    revenueOverTime: revenueResult.data.revenueOverTime || [],
                    avgRevenueByDay: revenueResult.data.avgRevenueByDay || [],
                    avgHourlySales: revenueResult.data.avgHourlySales || [],
                  };
                });

                // Cache revenue
                dispatch(
                  setCacheEntry({
                    key: revenueCacheKey,
                    data: { revenue: revenueResult.data },
                    branchIds: [...restaurantIds],
                  })
                );
                console.log("✅ [Overview] Revenue loaded - Charts displayed");
              }
            })
            .catch((err) => {
              console.error("❌ [Overview] Revenue error:", err);
              setLoadingRevenue(false);
            });

          // Fetch tables/order time - display conditional KPI as soon as ready
          loggedCloudRun("getDashboardTablesOrOrderTime", basePayload, {
            type: "main",
            context: "Overview-main-fetch",
          })
            .then((tablesOrOrderResult) => {
              console.log(
                "📥 [Overview] Tables/OrderTime response:",
                tablesOrOrderResult
              );
              setLoadingTablesOrOrder(false);

              // Verify restaurantIds haven't changed before processing response
              if (!verifyRestaurantIdsMatch()) {
                console.log(
                  "⏭️ [Overview] Ignoring tables/order response - restaurantIds changed"
                );
                return;
              }

              if (tablesOrOrderResult?.success && tablesOrOrderResult?.data) {
                // Update data with tables/order time using functional update
                setData((prevData) => {
                  // Only merge with prevData if it belongs to the same restaurantIds
                  const shouldUsePrevData =
                    prevData &&
                    dataRestaurantIdsRef.current === requestRestaurantIdsKey;
                  if (!shouldUsePrevData) {
                    dataRestaurantIdsRef.current = requestRestaurantIdsKey;
                  }
                  // If no base data yet, create minimal structure (will be updated when metrics load)
                  if (!prevData || !shouldUsePrevData) {
                    return {
                      totalRevenue: {
                        current: 0,
                        previous: 0,
                        change: 0,
                        label: null,
                      },
                      totalOrders: {
                        current: 0,
                        previous: 0,
                        change: 0,
                        label: null,
                      },
                      avgOrderValue: {
                        current: 0,
                        previous: 0,
                        change: 0,
                        label: null,
                      },
                      paymentMethods: [],
                      revenueOverTime: [],
                      avgRevenueByDay: [],
                      avgHourlySales: [],
                      topSellingItems: [],
                      // Determine hasDeliveryOrPickup from the response
                      hasDeliveryOrPickup:
                        !!tablesOrOrderResult.data.avgOrderTime,
                      ...(tablesOrOrderResult.data.avgOrderTime
                        ? {
                            avgOrderTime: tablesOrOrderResult.data.avgOrderTime,
                          }
                        : {
                            tablesOpenPerDay:
                              tablesOrOrderResult.data.tablesOpenPerDay,
                          }),
                    };
                  }
                  // Determine hasDeliveryOrPickup from the response
                  // If avgOrderTime exists, hasDeliveryOrPickup is true, otherwise false
                  const hasDeliveryOrPickup =
                    !!tablesOrOrderResult.data.avgOrderTime;

                  return {
                    ...prevData,
                    hasDeliveryOrPickup,
                    ...(hasDeliveryOrPickup
                      ? { avgOrderTime: tablesOrOrderResult.data.avgOrderTime }
                      : {
                          tablesOpenPerDay:
                            tablesOrOrderResult.data.tablesOpenPerDay,
                        }),
                  };
                });

                // Cache tables/order
                dispatch(
                  setCacheEntry({
                    key: tablesOrOrderCacheKey,
                    data: { tablesOrOrder: tablesOrOrderResult.data },
                    branchIds: [...restaurantIds],
                  })
                );
                console.log(
                  "✅ [Overview] Tables/OrderTime loaded - Conditional KPI displayed"
                );
              }
            })
            .catch((err) => {
              console.error("❌ [Overview] Tables/OrderTime error:", err);
              setLoadingTablesOrOrder(false);
            });

          // Fetch top items - always fetch 10 items to cover current page and next page
          // Fetch ALL top items (no limit/skip - backend returns all sorted data)
          loggedCloudRun(
            "getTopSellingItem",
            {
              ...basePayload,
              // No limit or skip - fetch all data
            },
            {
              type: "main",
              context: "Overview-main-fetch",
            }
          )
            .then((topItemsResult) => {
              console.log("📥 [Overview] Top items response:", topItemsResult);
              setLoadingTopItems(false);

              // Verify restaurantIds haven't changed before processing response
              if (!verifyRestaurantIdsMatch()) {
                console.log(
                  "⏭️ [Overview] Ignoring top items response - restaurantIds changed"
                );
                return;
              }

              // Extract all items from backend
              const allItems = normalizeTopItemsResponse(topItemsResult);

              // Extract total count from response if available
              let totalCount = allItems.length;
              if (topItemsResult?.data?.total !== undefined) {
                totalCount = topItemsResult.data.total;
              } else if (topItemsResult?.total !== undefined) {
                totalCount = topItemsResult.total;
              } else if (topItemsResult?.data?.count !== undefined) {
                totalCount = topItemsResult.data.count;
              }

              // Only update if data belongs to current restaurantIds
              if (dataRestaurantIdsRef.current === requestRestaurantIdsKey || dataRestaurantIdsRef.current === "") {
                // Store all items
                setAllTopItems(allItems);
                setTotalTopItems(totalCount);
                // Reset to page 1 when new data is loaded
                setCurrentPage(1);
                // Update tracking ref
                dataRestaurantIdsRef.current = requestRestaurantIdsKey;
              } else {
                console.warn(
                  "⚠️ [Overview] Ignoring top items - data belongs to different restaurantIds"
                );
                return;
              }

              console.log(
                `✅ [Overview] All items loaded: ${allItems.length} total items`
              );

              // Cache all items
              dispatch(
                setCacheEntry({
                  key: topItemsCacheKey,
                  data: { topSellingItems: allItems, total: totalCount },
                  branchIds: [...restaurantIds],
                })
              );
            })
            .catch((err) => {
              console.error("❌ [Overview] Top items error:", err);
              setLoadingTopItems(false);
              setTopItems([]);
            });
        }, 100); // Small delay (100ms) to prioritize metrics request
      } catch (error: any) {
        const errorMessage =
          error?.message || error?.toString() || "Failed to load data";
        setError(
          typeof errorMessage === "string" ? errorMessage : String(errorMessage)
        );
        console.error("❌ [Overview] Exception caught:", errorMessage, error);
        // Reset all loading states on error
        setLoadingMetrics(false);
        setLoadingRevenue(false);
        setLoadingTablesOrOrder(false);
        setLoadingTopItems(false);
        setLoadingState(false);
        dispatch(setLoading(false));
      }

      // Note: loadingState will be set to false when metrics load (critical data)
      // Other sections will display progressively as they load
      fetchingRef.current = false;
      // Reset force refresh flag after fetch completes
      forceRefreshRef.current = false;
    };

    // Fetch data (will use cache if available, so safe to call on every change)
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchKey, restaurantIdsKey, startDate, endDate, refreshTrigger]);

  // Sort allTopItems client-side based on sortState
  const sortedTopItems = useMemo(() => {
    if (!allTopItems || allTopItems.length === 0) {
      return allTopItems;
    }

    let sorted = [...allTopItems]; // Create a copy to avoid mutating original

    // Apply sorting if sortState is set
    if (sortState.column) {
      sorted.sort((a, b) => {
        let aValue: any;
        let bValue: any;

        switch (sortState.column) {
          case "name":
            aValue = (a.name || "").toLowerCase();
            bValue = (b.name || "").toLowerCase();
            break;
          case "category":
            aValue = (a.category || "").toLowerCase();
            bValue = (b.category || "").toLowerCase();
            break;
          case "revenue":
            aValue = a.revenue || a.price * a.orders || 0;
            bValue = b.revenue || b.price * b.orders || 0;
            break;
          case "amount":
            aValue = a.orders || 0;
            bValue = b.orders || 0;
            break;
          default:
            return 0;
        }

        // For string comparisons (name, category)
        if (sortState.column === "name" || sortState.column === "category") {
          if (sortState.direction === "asc") {
            return aValue.localeCompare(bValue);
          } else {
            return bValue.localeCompare(aValue);
          }
        }

        // For number comparisons (revenue, amount)
        if (sortState.direction === "asc") {
          return aValue - bValue;
        } else {
          return bValue - aValue;
        }
      });
    }

    return sorted;
  }, [allTopItems, sortState.column, sortState.direction]);

  // Client-side pagination: update displayed items when page or sortedTopItems changes
  useEffect(() => {
    if (!sortedTopItems || sortedTopItems.length === 0) {
      setTopItems([]);
      return;
    }

    // Calculate start and end indices for current page
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    // Get items for current page from sorted items - ensure we only get 5 items
    const currentPageItems = sortedTopItems.slice(startIndex, endIndex);

    // Ensure we never show more than itemsPerPage items
    const limitedItems = currentPageItems.slice(0, itemsPerPage);
    setTopItems(limitedItems);

    console.log(
      `✅ [Overview] Page ${currentPage}: showing items ${
        startIndex + 1
      }-${Math.min(endIndex, sortedTopItems.length)} of ${
        sortedTopItems.length
      } (displaying ${limitedItems.length} items)`
    );
  }, [currentPage, sortedTopItems, itemsPerPage]);

  // Save data to localStorage whenever all data is loaded (24h cache)
  // Use current timestamp (Date.now()) to ensure fresh 24h expiration from fetch time
  // IMPORTANT: Only save if data belongs to currently selected restaurantIds
  useEffect(() => {
    if (
      data &&
      !loadingMetrics &&
      !loadingRevenue &&
      !loadingTablesOrOrder &&
      !loadingTopItems &&
      restaurantIds &&
      restaurantIds.length > 0 &&
      startDate &&
      endDate
    ) {
      // Verify that the data belongs to the currently selected restaurantIds
      const currentIdsKey = Array.from(restaurantIds)
        .sort()
        .join(",");
      
      // Only save if data belongs to current selection
      if (dataRestaurantIdsRef.current !== currentIdsKey && dataRestaurantIdsRef.current !== "") {
        console.warn(
          "⚠️ [Overview] Skipping cache save - data belongs to different restaurantIds. Data:",
          dataRestaurantIdsRef.current,
          "Current:",
          currentIdsKey
        );
        return;
      }

      const overviewCacheKey = getCacheKey(
        "getDashboardOverview",
        restaurantIds,
        startDate,
        endDate
      );
      // Use current timestamp to ensure 24h cache from now
      const timestamp = Date.now();
      // Include topItems in the cached data
      const dataToCache = {
        ...data,
        topItems: allTopItems,
      };
      saveToLocalStorage(
        overviewCacheKey,
        dataToCache as OverviewData,
        timestamp,
        restaurantIds
      );
      console.log(
        "💾 [Overview] Data saved to 24h cache with current timestamp",
        "restaurantIds:",
        currentIdsKey
      );
    }
  }, [
    data,
    allTopItems,
    loadingMetrics,
    loadingRevenue,
    loadingTablesOrOrder,
    loadingTopItems,
    restaurantIds,
    startDate,
    endDate,
    saveToLocalStorage,
  ]);

  // Helper function to format the last updated time
  const getTimeAgo = (date: Date): string => {
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
      return diffInMinutes <= 1 ? "Just now" : `${diffInMinutes} Minutes ago`;
    } else if (diffInHours === 1) {
      return "1 Hour ago";
    } else if (diffInHours < 24) {
      return `${diffInHours} Hours ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return diffInDays === 1 ? "1 Day ago" : `${diffInDays} Days ago`;
    }
  };

  const handleRefresh = () => {
    // Clear only the current date range cache to force refetch
    if (restaurantIds && restaurantIds.length > 0 && startDate && endDate) {
      const overviewCacheKey = getCacheKey(
        "getDashboardOverview",
        restaurantIds,
        startDate,
        endDate
      );
      clearCurrentCacheOnly(overviewCacheKey);
      console.log(
        "🔄 [Overview] Refresh triggered - cleared current cache, will fetch live data"
      );
    }

    // Set force refresh flag to bypass cache checks
    forceRefreshRef.current = true;

    // Reset loading states and fetchingRef to trigger new fetch
    // Also reset lastFetchKeyRef to force a new fetch even if fetchKey is the same
    lastFetchKeyRef.current = "";
    fetchingRef.current = false;
    setLoadingMetrics(true);
    setLoadingRevenue(true);
    setLoadingTablesOrOrder(true);
    setLoadingTopItems(true);
    setLoadingState(true);

    // Trigger useEffect to run by incrementing refreshTrigger
    setRefreshTrigger((prev) => prev + 1);
  };

  // Show empty state only if branches have loaded AND no restaurants are selected
  if ((!restaurantIds || restaurantIds.length === 0) && !loading) {
    return (
      <Layout>
        <div className={styles.emptyState}>
          <div className={styles.emptyStateIcon}>📊</div>
          <h3>Select a Branch to View Overview Analytics</h3>
          <p>
            Please select a branch from the dropdown above to view overview data
          </p>
        </div>
      </Layout>
    );
  }

  // Don't show UI until we have data from backend
  // Show full page loading skeleton while waiting for data
  if (!data || loadingState) {
    return (
      <Layout>
        <div className={styles.container}>
          <KPISkeleton />
          <ChartSkeleton />
          <div className={styles.chartsGrid}>
            <ChartSkeleton />
            <ChartSkeleton />
          </div>
          <ListSkeleton />
          <ChartSkeleton />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={styles.container}>
        {error && (
          <div className={styles.errorBanner}>
            ⚠️ {error} - Showing cached data
          </div>
        )}

        {/* Update Banner */}
        <div className={styles.updateBanner}>
          <span className={styles.updateText}>
            Updated: {getTimeAgo(lastUpdated)}
          </span>
          <button
            className={styles.refreshButton}
            onClick={handleRefresh}
            disabled={loadingMetrics}
            aria-label="Refresh data"
          >
            <img
              src="/refresh-ccw-02.svg"
              alt="refresh"
              className={styles.refreshIcon}
            />
          </button>
        </div>

        {/* KPI Cards Row - Display when metrics are loaded */}
        {loadingMetrics ? (
          <KPISkeleton />
        ) : (
          <div className={styles.kpiGrid}>
            <KPICard
              title="Total Revenue"
              metric={{
                current: data?.totalRevenue?.current || 0,
                previous: data?.totalRevenue?.previous || 0,
                change: data?.totalRevenue?.change || 0,
                label: getComparisonLabel(selectedDateOption),
              }}
              currency
            />
            <KPICard
              title="Total Orders"
              metric={{
                current: data?.totalOrders?.current || 0,
                previous: data?.totalOrders?.previous || 0,
                change: data?.totalOrders?.change || 0,
                label: getComparisonLabel(selectedDateOption),
              }}
            />
            <KPICard
              title="AVG Order Value"
              metric={{
                current: data?.avgOrderValue?.current || 0,
                previous: data?.avgOrderValue?.previous || 0,
                change: data?.avgOrderValue?.change || 0,
                label: getComparisonLabel(selectedDateOption),
              }}
              currency
            />
            {/* Conditional KPI - show when tables/order data is loaded */}
            {data?.hasDeliveryOrPickup ? (
              // Show Avg Order Time if hasDeliveryOrPickup is true
              loadingTablesOrOrder || !data?.avgOrderTime ? (
                // Show skeleton while loading or if data not yet available
                <div style={{ minHeight: "120px" }}>
                  <KPISkeleton />
                </div>
              ) : (
                <KPICard
                  title="Avg Order Time"
                  metric={{
                    current: data?.avgOrderTime?.current || 0,
                    previous: data?.avgOrderTime?.previous || 0,
                    change: data?.avgOrderTime?.change || 0,
                    label: getComparisonLabel(selectedDateOption),
                  }}
                  timeUnit
                  invertTrend
                />
              )
            ) : // Show Tables Open/Day if hasDeliveryOrPickup is false
            loadingTablesOrOrder || !data?.tablesOpenPerDay ? (
              // Show skeleton while loading or if data not yet available
              <div style={{ minHeight: "120px" }}>
                <KPISkeleton />
              </div>
            ) : (
              <KPICard
                title="Tables Open/Day"
                metric={{
                  current: data?.tablesOpenPerDay?.current || 0,
                  previous: data?.tablesOpenPerDay?.previous || 0,
                  change: data?.tablesOpenPerDay?.change || 0,
                  label: getComparisonLabel(selectedDateOption),
                }}
              />
            )}
          </div>
        )}

        {/* Revenue Section - Display when revenue data is loaded */}
        {loadingRevenue ? (
          <>
            <ChartSkeleton />
            <div className={styles.chartsGrid}>
              <ChartSkeleton />
              <ChartSkeleton />
            </div>
          </>
        ) : (
          <>
            <div className={styles.revenueSection}>
              <h3 className={styles.revenueTitle}>Total Revenue</h3>
              <div className={styles.chartWrapper}>
                <LineChart
                  title=""
                  data={(() => {
                    const revenueData = data?.revenueOverTime || [];
                    // If no data, create multiple points with 0 value using the date range
                    if (revenueData.length === 0 && startDate && endDate) {
                      // Calculate number of days in the range
                      const start = new Date(startDate);
                      const end = new Date(endDate);
                      const daysDiff = Math.ceil(
                        (end.getTime() - start.getTime()) /
                          (1000 * 60 * 60 * 24)
                      );
                      // Create data points for each day in the range (max 30 points for performance)
                      const numPoints = Math.min(daysDiff, 30);
                      return Array.from({ length: numPoints }, (_, i) => {
                        const currentDate = new Date(start);
                        currentDate.setDate(start.getDate() + i);
                        // Format date as "MMM D" (e.g., "Jan 1")
                        const dateStr = currentDate.toLocaleDateString(
                          "en-US",
                          {
                            month: "short",
                            day: "numeric",
                          }
                        );
                        return {
                          date: dateStr,
                          value: 0,
                        };
                      });
                    }
                    return revenueData;
                  })()}
                  currency={currencySymbol}
                  color="#005073"
                />
              </div>
            </div>

            {/* Charts Grid - Average Revenue by Day and Average Hourly Sales */}
            <div className={styles.chartsGrid}>
              <div className={styles.chartItem}>
                <BarChart
                  title="Average Revenue by Day"
                  data={
                    (data?.avgRevenueByDay || []).length > 0
                      ? (data?.avgRevenueByDay || []).map((d) => ({
                          label: d.day.toUpperCase(),
                          value: d.value || 0,
                        }))
                      : [{ label: "No data", value: 0 }]
                  }
                  horizontal
                  backgroundColor="rgba(1, 87, 125, 0.40)"
                  hoverColor="#005073"
                  currency={currencySymbol}
                />
              </div>
              <div className={styles.chartItem}>
                <BarChart
                  title="Average Hourly Sales"
                  data={
                    (data?.avgHourlySales || []).length > 0
                      ? (data?.avgHourlySales || []).map((d) => ({
                          label: `${d.hour}:00`,
                          value: d.value || 0,
                        }))
                      : [{ label: "No data", value: 0 }]
                  }
                  backgroundColor="rgba(1, 87, 125, 0.40)"
                  hoverColor="#005073"
                />
              </div>
            </div>
          </>
        )}

        {/* Top Selling Items Section - Display when top items data is loaded */}
        {loadingTopItems ? (
          <ListSkeleton />
        ) : (
          <div className={styles.topItemsSection}>
            <h3 className={styles.sectionTitle}>Top Selling Items</h3>
            <div className={styles.topItemsTableContainer}>
              <table className={styles.topItemsTable}>
                <thead>
                  <tr>
                    <th className={styles.leftAlignHeader}>
                      <div
                        className={styles.headerContent}
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          const newDirection =
                            sortState.column === "name" &&
                            sortState.direction === "asc"
                              ? "dec"
                              : "asc";
                          setSortState({
                            column: "name",
                            direction: newDirection,
                          });
                          setCurrentPage(1); // Reset to first page when sorting
                        }}
                      >
                        Item{" "}
                        {sortState.column === "name" ? (
                          sortState.direction === "asc" ? (
                            <ChevronDownIcon />
                          ) : (
                            <ChevronUpIcon />
                          )
                        ) : (
                          <ChevronDownIcon />
                        )}
                      </div>
                    </th>
                    <th className={styles.centerAlignHeader}>
                      <div
                        className={styles.headerContent}
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          const newDirection =
                            sortState.column === "revenue" &&
                            sortState.direction === "asc"
                              ? "dec"
                              : "asc";
                          setSortState({
                            column: "revenue",
                            direction: newDirection,
                          });
                          setCurrentPage(1); // Reset to first page when sorting
                        }}
                      >
                        Revenue{" "}
                        {sortState.column === "revenue" ? (
                          sortState.direction === "asc" ? (
                            <ChevronDownIcon />
                          ) : (
                            <ChevronUpIcon />
                          )
                        ) : (
                          <ChevronDownIcon />
                        )}
                      </div>
                    </th>
                    <th className={styles.centerAlignHeader}>
                      <div
                        className={styles.headerContent}
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          const newDirection =
                            sortState.column === "category" &&
                            sortState.direction === "asc"
                              ? "dec"
                              : "asc";
                          setSortState({
                            column: "category",
                            direction: newDirection,
                          });
                          setCurrentPage(1); // Reset to first page when sorting
                        }}
                      >
                        Category{" "}
                        {sortState.column === "category" ? (
                          sortState.direction === "asc" ? (
                            <ChevronDownIcon />
                          ) : (
                            <ChevronUpIcon />
                          )
                        ) : (
                          <ChevronDownIcon />
                        )}
                      </div>
                    </th>
                    <th className={styles.centerAlignHeader}>
                      <div
                        className={styles.headerContent}
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          const newDirection =
                            sortState.column === "amount" &&
                            sortState.direction === "asc"
                              ? "dec"
                              : "asc";
                          setSortState({
                            column: "amount",
                            direction: newDirection,
                          });
                          setCurrentPage(1); // Reset to first page when sorting
                        }}
                      >
                        Amount{" "}
                        {sortState.column === "amount" ? (
                          sortState.direction === "asc" ? (
                            <ChevronDownIcon />
                          ) : (
                            <ChevronUpIcon />
                          )
                        ) : (
                          <ChevronDownIcon />
                        )}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {topItems && topItems.length > 0 ? (
                    <>
                      {topItems.map((item, index) => (
                        <tr key={item.id || item.name || `item-${index}`}>
                          <td className={styles.itemNameCell}>
                            <div className={styles.itemRow}>
                              <div className={styles.imageCell}>
                                <div className={styles.itemImage}>
                                  {item.image || item.coverPhoto ? (
                                    <img
                                      src={item.image || item.coverPhoto}
                                      alt={item.name}
                                    />
                                  ) : (
                                    <div className={styles.noImage}>
                                      No Image
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className={styles.itemNameWrapper}>
                                <div className={styles.itemName}>
                                  {item.name}
                                </div>
                                <div className={styles.itemDescription}>
                                  {item.description ||
                                    `${item.name} ingredients and pricing`}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className={styles.revenueCell}>
                            EGP{" "}
                            {item.revenue
                              ? item.revenue.toLocaleString()
                              : (item.price * item.orders).toLocaleString()}
                          </td>
                          <td className={styles.itemCategory}>
                            {item.category &&
                            typeof item.category === "string" &&
                            item.category.trim()
                              ? item.category.charAt(0).toUpperCase() +
                                item.category.slice(1).toLowerCase()
                              : "-"}
                          </td>
                          <td className={styles.volumeCell}>
                            {`${item.orders || 0} Item${
                              item.orders === 1 ? "" : "s"
                            }`}
                          </td>
                        </tr>
                      ))}
                    </>
                  ) : (
                    <tr>
                      <td colSpan={4} className={styles.emptyTopItemsCell}>
                        <div className={styles.emptyTopItemsPlaceholder}>
                          <div className={styles.emptyTopItemsIcon}>📦</div>
                          <h3 className={styles.emptyTopItemsTitle}>
                            No sales yet.
                          </h3>
                          <p className={styles.emptyTopItemsText}>
                            Start receiving orders to see your top selling items
                            here.
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              {/* Show pagination when we have more items than per page */}
              {(() => {
                const totalPages = Math.ceil(
                  (sortedTopItems?.length || 0) / itemsPerPage
                );
                return totalPages > 1 ? (
                  <div className={styles.pagination}>
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={(event, value) => setCurrentPage(value)}
                      shape="rounded"
                      showFirstButton={false}
                      showLastButton={false}
                      siblingCount={isMobile ? 0 : 1}
                      boundaryCount={isMobile ? 1 : 1}
                      sx={{
                        "& .MuiPaginationItem-root": {
                          minWidth: isMobile ? "32px" : "36px",
                          height: isMobile ? "32px" : "36px",
                          margin: isMobile ? "0 2px" : "0 4px",
                          borderRadius: "8px",
                          border: "1px solid #e5e7eb",
                          background: "transparent",
                          color: "#000000",
                          fontSize: isMobile ? "12px" : "14px",
                          fontWeight: 400,
                          "&:hover": {
                            background: "rgba(0, 0, 0, 0.04)",
                          },
                          "&.Mui-selected": {
                            background: "rgba(1, 87, 125, 0.15)",
                            color: "#005073",
                            borderColor: "rgba(1, 87, 125, 0.3)",
                            fontWeight: 600,
                            "&:hover": {
                              background: "rgba(1, 87, 125, 0.25)",
                            },
                          },
                          "&.Mui-disabled": {
                            opacity: 0.4,
                          },
                        },
                        "& .MuiPaginationItem-ellipsis": {
                          color: "#000000",
                          border: "none",
                          background: "transparent",
                        },
                      }}
                    />
                  </div>
                ) : null;
              })()}
            </div>
          </div>
        )}

        {/* Payment Methods Section - Only display if paymentMethods exists and has data */}
        {data?.paymentMethods && data.paymentMethods.length > 0 && (
          <div className={styles.paymentSection}>
            <DonutChart
              title="Payment Methods"
              data={(data?.paymentMethods || []).map((pm) => ({
                label: pm.method,
                value: pm.count,
                percentage: pm.percentage,
              }))}
              centerText="Total Payments"
              centerValue={(data?.paymentMethods || []).reduce(
                (sum, pm) => sum + pm.count,
                0
              )}
              colors={["#1E3A5F", "#F13C4F"]}
            />
          </div>
        )}

        {/* Footer */}
        <div className={styles.footer}>
          <div className={styles.footerContent}>
            <span className={styles.copyright}>© Copyrights 2025 EZeats</span>
            <div className={styles.footerLinks}>
              <a
                href={termsAndConditionsUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms & Conditions
              </a>
              <span>-</span>
              <a
                href={privacypolicyUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Overview;
